import { Button, Card, CardBody } from '@heroui/react';
import React, { useState, useEffect } from 'react';

import { useSupplierLinking } from '../../hooks';

import { LinkSupplierModal } from './LinkSupplierModal';
import { SupplierSelectOption } from './utils';

interface SupplierLinkingFieldProps {
  userId: string;
  userName: string;
  initialLinkedSupplier?: SupplierSelectOption | null;
  disabled?: boolean;
}

export const SupplierLinkingField: React.FC<SupplierLinkingFieldProps> = ({
  userId,
  userName,
  initialLinkedSupplier = null,
  disabled = false,
}) => {
  const [linkedSupplier, setLinkedSupplier] =
    useState<SupplierSelectOption | null>(initialLinkedSupplier);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { unlinkUserFromSupplier, isUnlinking } = useSupplierLinking();

  // Update local state when initial data changes
  useEffect(() => {
    setLinkedSupplier(initialLinkedSupplier);
  }, [initialLinkedSupplier]);

  const handleRemoveSupplier = async () => {
    try {
      // Call the actual supplier unlinking mutation
      await unlinkUserFromSupplier(userId);

      // Update local state
      setLinkedSupplier(null);
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  const handleModalSuccess = (selectedSupplier: SupplierSelectOption) => {
    // Update local state immediately
    setLinkedSupplier(selectedSupplier);
    setIsModalOpen(false);
    // TODO: Implement actual supplier linking mutation
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-foreground">
              Linked Distributor
            </h3>
            {linkedSupplier && (
              <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-success-700 bg-success-100 rounded-full">
                1
              </span>
            )}
          </div>
          <p className="text-sm text-default-500 mt-1">
            Link this user to a distributor to give them distributor-specific
            access.
          </p>
        </div>
        {!linkedSupplier && (
          <Button
            color="primary"
            variant="flat"
            size="sm"
            onPress={() => setIsModalOpen(true)}
            isDisabled={disabled}
          >
            + Link Distributor
          </Button>
        )}
      </div>

      {linkedSupplier ? (
        <Card className="shadow-sm">
          <CardBody className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-default-900 truncate">
                  {linkedSupplier.label}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <Button
                  color="primary"
                  variant="light"
                  size="sm"
                  onPress={() => setIsModalOpen(true)}
                  isDisabled={disabled || isUnlinking}
                >
                  Change
                </Button>
                <Button
                  color="danger"
                  variant="light"
                  size="sm"
                  onPress={handleRemoveSupplier}
                  isDisabled={disabled || isUnlinking}
                  isLoading={isUnlinking}
                >
                  Remove
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="text-center py-6 bg-default-50 rounded-lg border-2 border-dashed border-default-200">
          <p className="text-default-500 mb-2">
            This user is not linked to any distributor yet.
          </p>
          <p className="text-sm text-default-400">
            Click "Link Distributor" to search and link this user to a
            distributor.
          </p>
        </div>
      )}

      <LinkSupplierModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userId={userId}
        userName={userName}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};
